import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { Col, GetProp, message, Row, UploadProps, Switch, ColorPicker, Tooltip, CollapseProps, InputNumber } from 'antd';
import { Collapse } from 'antd';
import { useCallback, useMemo } from 'react';
import { CustomFile, ISunkhronosMobile } from '../utils';
import FAIcon from '../../../../common/FAIcon/FAIcon';
import UploadButton from '../../../../common/UploadButton/UploadButton';

interface Props {
    mobileData: ISunkhronosMobile;
    updateData: (data: ISunkhronosMobile) => void;
}

const MobileCompanyDrawer = (props: Props) => {
    type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
    const intl = useIntl();

    const getBase64 = useCallback((img: FileType, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result as string));
        reader.readAsDataURL(img);
    }, []);

    const beforeUpload = useCallback((file: FileType) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            message.error(intl.formatMessage({ defaultMessage: 'You can only upload JPG/PNG file!' }));
            return false;
        }

        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error(intl.formatMessage({ defaultMessage: 'Image must be smaller than 2MB!' }));
            return false;
        }

        // Convertir l'image en Base64
        getBase64(file, (base64Url) => {
            const customFile: CustomFile = file as CustomFile;
            customFile.blob = base64Url;  // Ajout du blob en Base64
            props.updateData({ ...props.mobileData, image: customFile });  // Mettre à jour l'état du parent avec l'image
        });

        return false;  // Empêche l'upload automatique, car nous gérons tout côté client
    }, [getBase64, intl, props]);

    const items: CollapseProps['items'] = useMemo(() => [
        {
            key: '1',
            label: intl.formatMessage({ defaultMessage: "More options" }),
            children: (
                <Row className='inline full-width centerHeight flex-around'>
                    <Col xs={{ span: 11 }}>
                        {props.mobileData.mobileDisplay.map(menuItem => (
                            <Col key={menuItem.key} className='flex-between mobile_bottom'>
                                <label>{intl.formatMessage(menuItem.name)}</label>
                                <Switch
                                    size='small'
                                    checked={menuItem.enabled ?? false}
                                    onChange={(checked) =>
                                        props.updateData({
                                            ...props.mobileData,
                                            mobileDisplay: props.mobileData.mobileDisplay.map(item =>
                                                item.key === menuItem.key ? { ...item, enabled: checked } : item
                                            )
                                        })
                                    }
                                />
                            </Col>
                        ))}
                    </Col>


                    <Col className='column ' xs={{ span: 11 }}>
                        <Col className='flex-between'>
                            <label>{intl.formatMessage({ defaultMessage: "Logo rectangle" })}</label>
                            <Switch
                                size='small'
                                checked={props.mobileData.logoRectangle}
                                onChange={(checked) => props.updateData({
                                    ...props.mobileData,
                                    logoRectangle: checked
                                })}
                            />
                        </Col>
                        <Col className='flex-between'>
                            <label>{intl.formatMessage({ defaultMessage: "Logo rounded" })}</label>
                            <Switch
                                size='small'
                                checked={props.mobileData.logoRounded}
                                onChange={(checked) => props.updateData({
                                    ...props.mobileData,
                                    logoRounded: checked
                                })}
                            />
                        </Col>
                        <Col className='flex-between centerHeight'>
                            <label>{intl.formatMessage({ defaultMessage: "Validity duration of the login" })}</label>
                            <InputNumber
                                value={props.mobileData.loginValidityDuration as number}
                                onChange={(value) =>
                                    props.updateData({ ...props.mobileData, loginValidityDuration: value ?? 0 })
                                }
                            />
                        </Col>
                    </Col>
                </Row>
            ),
        },
    ], [intl, props]);

    return (
        <>
            <Row className='full-width' gutter={[10, 10]}>
                <Col xs={{ span: 24 }}>
                    <h2><FAIcon prefix='fad' name='mobile-screen' /> <FormattedMessage defaultMessage="Mobile" /></h2>
                    <Col className='flex-between centerHeight'>
                        <Col>
                            <Tooltip title={intl.formatMessage({ defaultMessage: "Default: the logo of the web version" })}>
                                <UploadButton text={defineMessage({ defaultMessage: "Load mobile logo" })} beforeUpload={beforeUpload} file={props.mobileData.image} imageUpload={true} />
                            </Tooltip>
                        </Col>

                        <Col>
                            {props.mobileData.colorMenu.map(color => (
                                <div key={color.key} className="centerHeight mobile_bottom">
                                    <ColorPicker
                                        defaultValue={color.color}
                                        size='small'
                                        onChange={(value) => props.updateData({
                                            ...props.mobileData,
                                            colorMenu: props.mobileData.colorMenu.map(c =>
                                                c.key === color.key ? { ...c, color: value.toHexString() } : c
                                            )
                                        })}
                                        disabledAlpha
                                    />
                                    <span style={{ marginLeft: "1.5em" }}>{intl.formatMessage(color.label)}</span>
                                </div>
                            ))}
                        </Col>

                        <Col className='column' xs={{ span: 8 }}>
                            {props.mobileData.mobileMenu.map(menuItem => (
                                <Col key={menuItem.key} className='flex-between mobile_bottom'>
                                    <label>{intl.formatMessage(menuItem.name)}</label>
                                    <Switch
                                        size='small'
                                        checked={menuItem.enabled ?? false}
                                        onChange={(checked) => props.updateData({
                                            ...props.mobileData,
                                            mobileMenu: props.mobileData.mobileMenu.map(item =>
                                                item.key === menuItem.key ? { ...item, enabled: checked } : item
                                            )
                                        })}
                                    />
                                </Col>
                            ))}
                        </Col>
                    </Col>
                </Col>
            </Row >
            <Row gutter={[10, 10]}>
                <Collapse className='full-width' items={items} ghost />
            </Row>
        </>
    );
};

export default MobileCompanyDrawer;
